/**
 * Image Upload Service with Mandatory Geotag
 * 
 * This service provides centralized image upload functionality with automatic
 * geotag embedding. All image uploads through this service will have geotag
 * metadata automatically applied from the business profile coordinates.
 * 
 * Usage:
 * import ImageUploadService from '../services/ImageUploadService';
 * 
 * // Upload with auto-geotag
 * const result = await ImageUploadService.processImageForUpload(file, {
 *   businessData: locationData,
 *   // or provide coordinates directly:
 *   // coordinates: { latitude: 28.6139, longitude: 77.2090 }
 * });
 * 
 * // result.file contains the geotagged file
 * // result.geotagApplied tells if geotag was successfully embedded
 */

import { applyGeotagMetadata, normalizeGeotagMetadata, canEmbedGeotag } from '../utils/geotagging';
import logger from '../utils/logger';

/**
 * Extract coordinates from various business data structures
 */
const extractCoordinates = (businessData) => {
    if (!businessData) return null;

    let lat = null;
    let lng = null;

    // Source 1: latlng object (GMB format)
    if (businessData.latlng?.latitude && businessData.latlng?.longitude) {
        lat = businessData.latlng.latitude;
        lng = businessData.latlng.longitude;
        console.log('✅ [ImageUploadService] Found coords in latlng:', { lat, lng });
        return { lat, lng, source: 'latlng' };
    }

    // Source 2: location.latlng
    if (businessData.location?.latlng?.latitude && businessData.location?.latlng?.longitude) {
        lat = businessData.location.latlng.latitude;
        lng = businessData.location.latlng.longitude;
        console.log('✅ [ImageUploadService] Found coords in location.latlng:', { lat, lng });
        return { lat, lng, source: 'location.latlng' };
    }

    // Source 3: Direct lat/lng properties
    if (businessData.latitude != null && businessData.longitude != null) {
        lat = businessData.latitude;
        lng = businessData.longitude;
        console.log('✅ [ImageUploadService] Found coords in direct props:', { lat, lng });
        return { lat, lng, source: 'direct' };
    }

    // Source 4: lat/lng shorthand
    if (businessData.lat != null && businessData.lng != null) {
        lat = businessData.lat;
        lng = businessData.lng;
        console.log('✅ [ImageUploadService] Found coords in lat/lng:', { lat, lng });
        return { lat, lng, source: 'lat/lng' };
    }

    // Source 5: storefrontAddress
    if (businessData.storefrontAddress?.latitude && businessData.storefrontAddress?.longitude) {
        lat = businessData.storefrontAddress.latitude;
        lng = businessData.storefrontAddress.longitude;
        console.log('✅ [ImageUploadService] Found coords in storefrontAddress:', { lat, lng });
        return { lat, lng, source: 'storefrontAddress' };
    }

    // Source 6: metadata.latlng
    if (businessData.metadata?.latlng?.latitude && businessData.metadata?.latlng?.longitude) {
        lat = businessData.metadata.latlng.latitude;
        lng = businessData.metadata.latlng.longitude;
        console.log('✅ [ImageUploadService] Found coords in metadata:', { lat, lng });
        return { lat, lng, source: 'metadata' };
    }

    // Source 7: geometry.location (Google Places format)
    if (businessData.geometry?.location) {
        const geoLoc = businessData.geometry.location;
        lat = typeof geoLoc.lat === 'function' ? geoLoc.lat() : geoLoc.lat;
        lng = typeof geoLoc.lng === 'function' ? geoLoc.lng() : geoLoc.lng;
        if (lat != null && lng != null) {
            console.log('✅ [ImageUploadService] Found coords in geometry:', { lat, lng });
            return { lat, lng, source: 'geometry' };
        }
    }

    console.warn('⚠️ [ImageUploadService] No coordinates found in business data');
    return null;
};

/**
 * Build geotag metadata from business data
 */
const buildGeotagMetadata = (businessData, coordinates) => {
    const businessName = businessData?.title || businessData?.name || businessData?.businessName || '';
    const category = businessData?.category || businessData?.categories?.primaryCategory?.displayName || '';
    const city = businessData?.storefrontAddress?.locality || businessData?.city || businessData?.address?.city || '';
    const keywords = businessData?.selectedKeywords || businessData?.keywords || [businessName, category].filter(Boolean);

    const descParts = [businessName, category, city].filter(Boolean);
    const description = descParts.length > 0 ? descParts.join(' - ') : 'Business photo';

    return normalizeGeotagMetadata({
        latitude: coordinates.lat,
        longitude: coordinates.lng,
        keywords: Array.isArray(keywords) ? keywords : [],
        description
    });
};

/**
 * Process an image file and apply geotag before upload
 * 
 * @param {File} file - The image file to process
 * @param {Object} options - Options for processing
 * @param {Object} options.businessData - Business/location data to extract coordinates from
 * @param {Object} options.coordinates - Direct coordinates { latitude, longitude }
 * @param {Array} options.keywords - Optional keywords to embed
 * @param {string} options.description - Optional description to embed
 * @returns {Promise<{file: File, geotagApplied: boolean, coordinates: Object|null, error: string|null}>}
 */
const processImageForUpload = async (file, options = {}) => {
    console.log('📷 [ImageUploadService] =====================================');
    console.log('📷 [ImageUploadService] Processing image for upload with geotag');
    console.log('📷 [ImageUploadService] File:', file?.name, file?.size, 'bytes', file?.type);

    // Validate file
    if (!file) {
        console.error('❌ [ImageUploadService] No file provided');
        return { file: null, geotagApplied: false, coordinates: null, error: 'No file provided' };
    }

    // Check if file type supports geotagging
    if (!canEmbedGeotag(file)) {
        console.warn('⚠️ [ImageUploadService] File type does not support geotag:', file.type);
        return { file, geotagApplied: false, coordinates: null, error: 'Unsupported file type for geotag' };
    }

    // Get coordinates
    let coordinates = null;

    // Priority 1: Direct coordinates provided
    if (options.coordinates?.latitude != null && options.coordinates?.longitude != null) {
        coordinates = {
            lat: options.coordinates.latitude,
            lng: options.coordinates.longitude
        };
        console.log('✅ [ImageUploadService] Using provided coordinates:', coordinates);
    }
    // Priority 2: Extract from business data
    else if (options.businessData) {
        coordinates = extractCoordinates(options.businessData);
    }

    // If no coordinates found
    if (!coordinates) {
        console.warn('⚠️ [ImageUploadService] NO COORDINATES AVAILABLE');
        console.warn('⚠️ [ImageUploadService] Image will be returned WITHOUT geotag');
        console.log('📷 [ImageUploadService] =====================================');
        return { file, geotagApplied: false, coordinates: null, error: 'No coordinates available' };
    }

    // Build metadata
    const metadata = options.metadata || buildGeotagMetadata(options.businessData || {}, coordinates);

    // Override with provided keywords/description
    if (options.keywords) {
        metadata.keywords = options.keywords;
    }
    if (options.description) {
        metadata.description = options.description;
    }

    console.log('📍 [ImageUploadService] Geotag metadata:', metadata);

    // Apply geotag
    try {
        const result = await applyGeotagMetadata(file, metadata);

        if (result.applied && result.file) {
            console.log('✅ [ImageUploadService] GEOTAG EMBEDDED SUCCESSFULLY!');
            console.log('✅ [ImageUploadService] Original size:', file.size, 'bytes');
            console.log('✅ [ImageUploadService] New size:', result.file.size, 'bytes');
            console.log('✅ [ImageUploadService] Coordinates:', coordinates.lat, ',', coordinates.lng);
            console.log('📷 [ImageUploadService] =====================================');

            return {
                file: result.file,
                geotagApplied: true,
                coordinates: { latitude: coordinates.lat, longitude: coordinates.lng },
                error: null
            };
        } else {
            console.warn('⚠️ [ImageUploadService] Geotag embedding failed:', result.reason);
            console.log('📷 [ImageUploadService] =====================================');

            return {
                file,
                geotagApplied: false,
                coordinates: { latitude: coordinates.lat, longitude: coordinates.lng },
                error: result.reason || 'Embedding failed'
            };
        }
    } catch (error) {
        console.error('❌ [ImageUploadService] Error applying geotag:', error);
        logger.error('[ImageUploadService] Geotag error:', error);
        console.log('📷 [ImageUploadService] =====================================');

        return {
            file,
            geotagApplied: false,
            coordinates: { latitude: coordinates.lat, longitude: coordinates.lng },
            error: error.message
        };
    }
};

/**
 * Process multiple images with geotag
 */
const processMultipleImagesForUpload = async (files, options = {}) => {
    console.log('📷 [ImageUploadService] Processing', files.length, 'images with geotag');

    const results = [];
    for (const file of files) {
        const result = await processImageForUpload(file, options);
        results.push(result);
    }

    const successCount = results.filter(r => r.geotagApplied).length;
    console.log('📷 [ImageUploadService] Geotag applied to', successCount, '/', files.length, 'images');

    return results;
};

/**
 * Get current business coordinates from various sources
 */
const getCurrentBusinessCoordinates = () => {
    // Try to get from session/localStorage
    const sources = [
        () => {
            const session = JSON.parse(sessionStorage.getItem('session') || '{}');
            return session?.gmbPrimaryLocation?.latlng;
        },
        () => {
            const location = JSON.parse(sessionStorage.getItem('selectedLocation') || '{}');
            return location?.latlng;
        },
        () => {
            const business = JSON.parse(localStorage.getItem('selectedBusiness') || '{}');
            return business?.latlng;
        }
    ];

    for (const source of sources) {
        try {
            const coords = source();
            if (coords?.latitude && coords?.longitude) {
                return { latitude: coords.latitude, longitude: coords.longitude };
            }
        } catch (e) {
            // Continue to next source
        }
    }

    return null;
};

const ImageUploadService = {
    processImageForUpload,
    processMultipleImagesForUpload,
    extractCoordinates,
    buildGeotagMetadata,
    getCurrentBusinessCoordinates
};

export default ImageUploadService;
